import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ====================
// Types
// ====================
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string, formData?: any) => string | undefined;
  message?: string;
}

export interface ValidationRules {
  [key: string]: ValidationRule;
}

export interface ValidationContextType {
  errors: { [key: string]: string };
  isValid: boolean;
  validateField: (field: string, value: string) => void;
  clearFieldError: (field: string) => void;
  getFieldError: (field: string) => string | undefined;
  hasFieldError: (field: string) => boolean;
  validateFormData: (formData: any) => boolean;
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  clearAllErrors: () => void;
}

// ====================
// Validation Functions
// ====================
export const validateField = (
  field: string,
  value: string,
  formData: any,
  rules: ValidationRules
): string | undefined => {
  const rule = rules[field];
  if (!rule) return undefined;

  // Required validation
  if (rule.required && (!value || value.trim() === '')) {
    const fieldLabel = field.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, str => str.toUpperCase());
    return rule.message || `${fieldLabel} is required`;
  }

  // Skip other validations if value is empty and not required
  if (!value || value.trim() === '') return undefined;

  // Min length validation
  if (rule.minLength && value.length < rule.minLength) {
    return rule.message || `Must be at least ${rule.minLength} characters`;
  }

  // Max length validation
  if (rule.maxLength && value.length > rule.maxLength) {
    return rule.message || `Must be no more than ${rule.maxLength} characters`;
  }

  // Pattern validation
  if (rule.pattern && !rule.pattern.test(value)) {
    if (field === 'email') return rule.message || 'Please enter a valid email address';
    if (field === 'mobileNo') return rule.message || 'Please enter a valid 10-digit mobile number';
    return rule.message || 'Invalid format';
  }

  // Custom validation
  if (rule.custom) {
    return rule.custom(value, formData);
  }

  return undefined;
};

export const validateForm = (
  formData: any,
  rules: ValidationRules
): { isValid: boolean; errors: { [key: string]: string } } => {
  const errors: { [key: string]: string } = {};
  let isValid = true;

  for (const field of Object.keys(rules)) {
    const error = validateField(field, formData[field] || '', formData, rules);
    if (error) {
      errors[field] = error;
      isValid = false;
    }
  }

  return { isValid, errors };
};

// ====================
// Common Validation Rules
// ====================
export const commonValidationRules: ValidationRules = {
  fullName: {
    required: true,
    minLength: 2,
    maxLength: 50,
    message: 'Full name must be between 2-50 characters'
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  mobileNo: {
    required: true,
    pattern: /^[6-9]\d{9}$/,
    message: 'Please enter a valid 10-digit mobile number'
  },
  username: {
    required: true,
    minLength: 3,
    maxLength: 20,
    message: 'Username must be between 3-20 characters'
  },
  password: {
    minLength: 6,
    message: 'Password must be at least 6 characters',
    custom: (value, formData) => {
      if (!formData.id && !value) {
        return 'Password is required';
      }
      return undefined;
    },
  },
  confirmPassword: {
    custom: (value: string, formData: any) => {
      if (formData.password || !formData.id) {
        if (!value) {
          return 'Please confirm the password';
        }
        if (value !== formData.password) {
          return 'Passwords do not match';
        }
      }
      return undefined;
    }
  },
  dateOfBirth: {
    required: true,
    message: 'Date of birth is required'
  },
  role: {
    required: true,
    message: 'Role is required'
  },
  assignedHub: {
    required: true,
    message: 'Assigned hub is required'
  },
  profilePhoto: {
    required: true,
    message: 'Profile photo is required'
  },
  status: {
    required: true,
    message: 'Status is required'
  },
  emergencyContact: {
    required: true,
    pattern: /^[6-9]\d{9}$/,
    message: 'Please enter a valid 10-digit emergency contact number'
  }
};

// ====================
// Context
// ====================
const ValidationContext = createContext<ValidationContextType | undefined>(undefined);

// ====================
// Hook
// ====================
export const useFormValidation = (
  initialFormData: any,
  validationRules: ValidationRules = commonValidationRules
): ValidationContextType => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Validate entire form
  const validateFormData = useCallback((formData: any) => {
    const validation = validateForm(formData, validationRules);
    setErrors(validation.errors);
    return validation.isValid;
  }, [validationRules]);

  // Validate individual field
  const validateFieldValue = useCallback((field: string, value: string) => {
    const error = validateField(field, value, initialFormData, validationRules);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field] = error;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  }, [initialFormData, validationRules]);

  // Clear field error
  const clearFieldError = useCallback((field: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Get error for specific field
  const getFieldError = useCallback((field: string) => {
    return errors[field] || undefined;
  }, [errors]);

  // Check if field has error
  const hasFieldError = useCallback((field: string) => {
    return !!errors[field];
  }, [errors]);

  // Calculate if form is valid
  const isValid = Object.keys(errors).length === 0;

  return {
    errors,
    isValid,
    validateField: validateFieldValue,
    clearFieldError,
    getFieldError,
    hasFieldError,
    validateFormData,
    setErrors,
    clearAllErrors,
  };
};

// ====================
// Provider Component
// ====================
interface FormValidationProviderProps {
  children: React.ReactNode;
  formData: any;
  validationRules?: ValidationRules;
  onValidationChange?: (isValid: boolean, errors: { [key: string]: string }) => void;
}

export const FormValidationProvider: React.FC<FormValidationProviderProps> = ({
  children,
  formData,
  validationRules = commonValidationRules,
  onValidationChange
}) => {
  const validation = useFormValidation(formData, validationRules);

  // Notify parent of validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.errors);
    }
  }, [validation.isValid, validation.errors, onValidationChange]);

  return (
    <ValidationContext.Provider value={validation}>
      {children}
    </ValidationContext.Provider>
  );
};

// ====================
// Hook to use validation context
// ====================
export const useValidation = (): ValidationContextType => {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error('useValidation must be used within a FormValidationProvider');
  }
  return context;
};

// ====================
// Higher Order Component
// ====================
export const withFormValidation = <P extends object>(
  Component: React.ComponentType<P>,
  validationRules?: ValidationRules
) => {
  const WrappedComponent = React.forwardRef<any, P & { formData: any; onValidationChange?: (isValid: boolean, errors: { [key: string]: string }) => void }>((props, ref) => {
    const { formData, onValidationChange, ...restProps } = props as any;
    
    return (
      <FormValidationProvider
        formData={formData}
        validationRules={validationRules}
        onValidationChange={onValidationChange}
      >
        <Component {...(restProps as P)} ref={ref} />
      </FormValidationProvider>
    );
  });
  
  WrappedComponent.displayName = `withFormValidation(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default FormValidationProvider;
